import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BundleItem {
  product_id: string;
  quantity: number;
  sort_order: number;
  /** Resolved title from producer_products (for display) */
  title?: string;
  /** Resolved price in öre */
  price_ore?: number;
}

/**
 * Fetch and manage bundle items for a bundle product.
 * Resolves child product titles for display.
 */
export function useBundleItems(productId: string | undefined, enabled = true) {
  const [items, setItems] = useState<BundleItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!productId || !enabled) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_bundle_items')
        .select('product_id, quantity, sort_order')
        .eq('bundle_product_id', productId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const rawItems = (data || []) as BundleItem[];

      // Resolve titles
      if (rawItems.length > 0) {
        const ids = rawItems.map(i => i.product_id);
        const { data: products } = await supabase
          .from('producer_products')
          .select('id, title, price_ore')
          .in('id', ids);

        const productMap = new Map((products || []).map(p => [p.id, p]));
        const resolved = rawItems.map(item => ({
          ...item,
          title: productMap.get(item.product_id)?.title || 'Okänd produkt',
          price_ore: productMap.get(item.product_id)?.price_ore || 0,
        }));
        setItems(resolved);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('[useBundleItems]', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [productId, enabled]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, refetch: fetchItems, setItems };
}

/**
 * Fetch bundle items for public display (read-only, uses anon key).
 */
export function usePublicBundleItems(productId: string | undefined, isBundle: boolean) {
  const [items, setItems] = useState<BundleItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId || !isBundle) {
      setItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('product_bundle_items')
          .select('product_id, quantity, sort_order')
          .eq('bundle_product_id', productId)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        if (cancelled) return;

        const rawItems = (data || []) as BundleItem[];

        if (rawItems.length > 0) {
          const ids = rawItems.map(i => i.product_id);
          const { data: products } = await supabase
            .from('producer_products')
            .select('id, title, price_ore')
            .in('id', ids);

          const productMap = new Map((products || []).map(p => [p.id, p]));
          setItems(rawItems.map(item => ({
            ...item,
            title: productMap.get(item.product_id)?.title || 'Okänd produkt',
            price_ore: productMap.get(item.product_id)?.price_ore || 0,
          })));
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error('[usePublicBundleItems]', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [productId, isBundle]);

  return { items, loading };
}
